import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useT } from '../../i18n/LanguageContext';
import './Navbar.css';

function Navbar() {
  const { t, lang, setLang } = useT();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  const NAV_LINKS = [
    { label: t('nav.home'), to: '/',        type: 'router' },
    { label: t('nav.tour'), to: '/eventos', type: 'router' },
    { label: t('nav.music'), to: '/videos', type: 'router' },
    { label: t('nav.bio'),  to: '/aboutme', type: 'router' },
    { label: t('nav.shop'), to: 'https://masteredtrax.shop', type: 'external' },
  ];

  const marquee = t('nav.marquee');

  const close = () => setOpen(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => setOpen(false);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      {/* Top marquee strip */}
      <div className="ck-topstrip" aria-hidden="true">
        <div className="ck-topstrip__track">
          {Array.from({ length: 2 }).map((_, i) => (
            <div className="ck-topstrip__group" key={i}>
              {Array.isArray(marquee) && marquee.map((m, j) => (
                <span key={j}>{m}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <nav className={`ck-nav ${scrolled ? 'ck-nav--scrolled' : ''} ${open ? 'ck-nav--open' : ''}`}>
        <div className="ck-nav__inner">
          <Link to="/" className="ck-nav__logo" onClick={close} aria-label="C-Kan">
            <img src={process.env.PUBLIC_URL + '/assets/nav-logo.png'} alt="C-Kan" />
            <span className="ck-nav__logo-mark">{t('nav.official').toUpperCase()}</span>
          </Link>

          <ul className="ck-nav__links">
            {NAV_LINKS.map(link => (
              <li key={link.label}>
                {link.type === 'external' ? (
                  <a href={link.to} target="_blank" rel="noopener noreferrer" className="ck-nav__link">
                    {link.label}
                  </a>
                ) : (
                  <Link
                    to={link.to}
                    className={`ck-nav__link ${pathname.toLowerCase() === link.to.toLowerCase() ? 'ck-nav__link--active' : ''}`}
                  >
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          <div className="ck-nav__actions">
            {/* Language switcher */}
            <div className="ck-lang" role="group" aria-label={t('lang.aria')}>
              <button
                type="button"
                className={`ck-lang__btn ${lang === 'es' ? 'is-active' : ''}`}
                onClick={() => setLang('es')}
                aria-pressed={lang === 'es'}
              >
                ES
              </button>
              <span className="ck-lang__sep" aria-hidden="true">/</span>
              <button
                type="button"
                className={`ck-lang__btn ${lang === 'en' ? 'is-active' : ''}`}
                onClick={() => setLang('en')}
                aria-pressed={lang === 'en'}
              >
                EN
              </button>
            </div>

            <a
              href="https://masteredtrax.shop"
              target="_blank"
              rel="noopener noreferrer"
              className="ck-nav__cta"
            >
              <span>{t('nav.bookingCta')}</span>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </a>

            <button
              type="button"
              className="ck-nav__burger"
              aria-label={open ? t('nav.menuClose') : t('nav.menuOpen')}
              aria-expanded={open}
              onClick={() => setOpen(v => !v)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </nav>

      {/* Fullscreen mobile overlay — sibling of nav, not child, so no clipping */}
      <div
        className={`ck-nav__overlay ${open ? 'is-open' : ''}`}
        aria-hidden={!open}
        role="dialog"
        aria-modal="true"
      >
        <ul className="ck-nav__overlay-links">
          {NAV_LINKS.map((link, i) => (
            <li key={link.label} style={{ '--d': `${i * 60}ms` }}>
              {link.type === 'external' ? (
                <a href={link.to} target="_blank" rel="noopener noreferrer" onClick={close}>
                  <span className="idx">0{i + 1}</span>
                  {link.label}
                </a>
              ) : (
                <Link to={link.to} onClick={close}>
                  <span className="idx">0{i + 1}</span>
                  {link.label}
                </Link>
              )}
            </li>
          ))}
        </ul>

        <div className="ck-nav__overlay-foot">
          <p>{t('nav.bookingManagement')}</p>
          <a href="mailto:booking@masteredtrax.com">booking@masteredtrax.com</a>
          <a href="mailto:joe@masteredtrax.com">joe@masteredtrax.com</a>
        </div>
      </div>
    </>
  );
}

export default Navbar;
